import { createComponent as _$createComponent } from "solid-js/web";
/**
  * @class mmodSolidModule
  * @extends msysListener
  * @description
  * Base class for Solid-rendered GUI modules. Mosy-equivalent of mmodModule, but instead
  * of owning a `mele-window` and mutating DOM via attribute setters, it owns a Solid store
  * (source of truth) and mounts a Solid render root that projects the store to the DOM.
  * No mele elements are involved — views are plain Solid components.
  *
  * Subclasses override:
  *   - `initialStore()` to declare initial state (must spread super.initialStore())
  *   - `view()` to return JSX
  *   - any `mm<Action>(sender, params)` message handlers, which should mutate via this.setStore(...)
  *
  * The class honors the same msysWM contract as mmodModule — listens for mmWindow,
  * publishes mmWindowSet. msysWM does not need to change, so Solid modules run in
  * parallel with legacy mele modules in the same app.
  *
  * Built-in facilities every module gets:
  *   - reactive translations: `t(key)` / `tx(template)` over `store.language` (`useLang()` in views)
  *   - modal dialogs: `confirmModal()` / `alertModal()` through the `<ModalHost/>` component
  *   - generic components in ./components: ModuleRoot, ModalHost, Drawer, Lightbox
  *   - µs-datetime helpers in ./datetime
  *
  * Descendant components reach the owning module via the exported `useMosy()` primitive
  * (publish/request/store/setStore + helpers); `MosyProvider` is mounted automatically.
  */

import msysListener from 'msysListener';
import { createContext, useContext } from 'solid-js';
import { createStore } from 'solid-js/store';
import { render } from 'solid-js/web';
import './mmodSolidModule.scss';
const MosyContext = createContext(null);

/**
 * Bridge between a Solid component tree and the mmodSolidModule instance that owns it.
 * Mounted by mmodSolidModule.mmWindow() once, wrapping the subclass view.
 */
export function MosyProvider(props) {
  const m = props.module;
  const ctx = {
    // messaging
    publish: (target, action, params) => m.publish(target, action, params),
    publishSelf: (action, params) => m.publish('#ME', action, params),
    request: (target, action, params) => m.request(target, action, params),
    // state
    get store() {
      return m.store;
    },
    setStore: m.setStore,
    // identity
    listenerId: m.listener_id,
    // helpers
    module: m
  };
  return _$createComponent(MosyContext.Provider, {
    value: ctx,
    get children() {
      return props.children;
    }
  });
}

/** Read the module context from inside a module's view. Throws outside a MosyProvider. */
export function useMosy() {
  const ctx = useContext(MosyContext);
  if (!ctx) {
    throw new Error('useMosy() must be called inside a <MosyProvider> (mmodSolidModule wraps the view automatically)');
  }
  return ctx;
}

/** Translation hook for views: `const { t, tx } = useLang()` — reactive via store.language. */
export function useLang() {
  const {
    module
  } = useMosy();
  return {
    t: key => module.t(key),
    tx: text => module.tx(text)
  };
}
export default class mmodSolidModule extends msysListener {
  constructor(id, cfg, cfg_schema, dependencies) {
    dependencies = _.union(['msysWM'], dependencies);
    cfg_schema = _.extend({
      order: {
        type: 'number',
        default: 0,
        description: 'Order of the module in ATB.'
      },
      screen: {
        type: 'number',
        default: 0,
        description: 'Index of screen where the window shall be placed'
      },
      init: {
        type: 'object',
        default: {
          type: 'none'
        },
        description: 'Initial window setup; same structure as required by `mmWindowSet`'
      }
    }, cfg_schema);
    super(id, cfg, cfg_schema, dependencies);
    const [store, setStore] = createStore(this.initialStore());
    this.store = store;
    this.setStore = setStore;
  }

  /**
   * Subclasses override and spread super.initialStore() to add their own fields.
   */
  initialStore() {
    return {
      window: {
        x: 0,
        y: 0,
        width: 0,
        height: 0,
        z: 0,
        focused: false,
        behavior: 'desktop',
        orientation: 'landscape',
        visible: false
      },
      language: 'en',
      theme: 'day',
      mode: 'normal',
      modal: null // ModalHost state: { title, text, kind: 'confirm'|'alert', danger }
    };
  }

  /**
   * Subclasses override to return JSX. Default renders nothing.
   */
  view() {
    return null;
  }

  // ---- translations ----

  /** Reactive translation: depends on store.language, so views re-render on mmSetLanguage. */
  t(key) {
    return LANG.translate(this.store.language, `{{${key}}}`);
  }

  /** Translate a template STRING (e.g. server warnings: '{{key}}: detail') — resolves each {{}} part. */
  tx(text) {
    return LANG.translate(this.store.language, _.toString(text));
  }

  // ---- modal dialogs (rendered by the <ModalHost/> component from ./components) ----

  /** Confirm dialog; resolves true/false. */
  confirmModal(title, text, opts = {}) {
    return new Promise(resolve => {
      this._modal_resolve = resolve;
      this.setStore('modal', {
        title,
        text,
        kind: 'confirm',
        danger: !!opts.danger
      });
    });
  }

  /** Info/error dialog (single OK button). */
  alertModal(title, text) {
    this.setStore('modal', {
      title,
      text,
      kind: 'alert',
      danger: false
    });
  }
  closeModal(result) {
    this.setStore('modal', null);
    const resolve = this._modal_resolve;
    delete this._modal_resolve;
    if (resolve) resolve(!!result);
  }

  // ---- lifecycle ----

  mmStart(sender, params) {
    if (this.config.disabled) {
      // disabled module reports as started immediately
      return super.mmStart({
        listener_id: this.listener_id
      }, params || {});
    }
    if (!this.el) {
      // first call — kick off the WM dance; super.mmStart fires later from mmWindow
      this.publish('msysWM', 'mmWindowSet', this.config.init);
    } else {
      // re-entry (subclass or external) — render root exists, finalize start
      super.mmStart({
        listener_id: this.listener_id
      }, params || {});
    }
  }
  mmExit(sender, params) {
    if (this._dispose) {
      this._dispose();
      delete this._dispose;
    }
    if (this.el && this.el.parentNode) {
      this.el.parentNode.removeChild(this.el);
    }
    delete this.el;
    super.mmExit(sender, params);
  }

  // ---- msysWM contract ----

  mmWindow(sender, params) {
    var _params$x2, _params$y2, _params$width3, _params$height3, _params$z2, _params$width4, _params$height4;
    if (!this.el) {
      var _params$x, _params$y, _params$width, _params$height, _params$z, _params$width2, _params$height2;
      const doc = params.doc || document;
      this.el = doc.createElement('div');
      this.el.className = 'mmod';
      this.el.dataset.mod = this.listener_id;
      doc.body.appendChild(this.el);
      this.setStore('window', {
        x: (_params$x = params.x) !== null && _params$x !== void 0 ? _params$x : 0,
        y: (_params$y = params.y) !== null && _params$y !== void 0 ? _params$y : 0,
        width: (_params$width = params.width) !== null && _params$width !== void 0 ? _params$width : 0,
        height: (_params$height = params.height) !== null && _params$height !== void 0 ? _params$height : 0,
        z: (_params$z = params.z) !== null && _params$z !== void 0 ? _params$z : 0,
        focused: !!params.focused,
        behavior: params.behavior || this.store.window.behavior,
        orientation: params.orientation || this.store.window.orientation,
        visible: ((_params$width2 = params.width) !== null && _params$width2 !== void 0 ? _params$width2 : 0) > 0 && ((_params$height2 = params.height) !== null && _params$height2 !== void 0 ? _params$height2 : 0) > 0
      });
      this._dispose = render(() => {
        const _self$ = this;
        return _$createComponent(MosyProvider, {
          module: _self$,
          get children() {
            return _self$.view();
          }
        });
      }, this.el);

      // CRITICAL: msysListener.mmStart returns early unless sender.listener_id === this.listener_id
      // Always pass self-sender, never propagate the mmWindow sender (msysWM).
      super.mmStart({
        listener_id: this.listener_id
      }, params || {});
      return;
    }
    this.setStore('window', {
      x: (_params$x2 = params.x) !== null && _params$x2 !== void 0 ? _params$x2 : this.store.window.x,
      y: (_params$y2 = params.y) !== null && _params$y2 !== void 0 ? _params$y2 : this.store.window.y,
      width: (_params$width3 = params.width) !== null && _params$width3 !== void 0 ? _params$width3 : this.store.window.width,
      height: (_params$height3 = params.height) !== null && _params$height3 !== void 0 ? _params$height3 : this.store.window.height,
      z: (_params$z2 = params.z) !== null && _params$z2 !== void 0 ? _params$z2 : this.store.window.z,
      focused: !!params.focused,
      visible: ((_params$width4 = params.width) !== null && _params$width4 !== void 0 ? _params$width4 : this.store.window.width) > 0 && ((_params$height4 = params.height) !== null && _params$height4 !== void 0 ? _params$height4 : this.store.window.height) > 0
    });
  }

  // ---- app-globals broadcast by msysWM via #ALLWITHME ----

  mmSetLanguage(sender, params) {
    this.setStore('language', params.lang);
  }
  mmSetTheme(sender, params) {
    this.setStore('theme', params.theme);
  }
  mmSetBehavior(sender, params) {
    this.setStore('window', 'behavior', params.behavior);
  }
  mmMode(sender, params) {
    this.setStore('mode', params.mode);
  }
} // mmodSolidModule